import React, { useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';

import { rootUiActions } from 'app-components/flux/root-ui';
import { scheduledTransactionsActions } from 'app-components/flux/scheduled-transactions';
import { billPresentmentActions, billPresentmentSelectors } from 'data/billPresentment/slice';
import type { Biller, EnrollResponse } from 'data/billPresentment/types';

import StdDialog from 'components/Dialogs/StdDialog';
import { useAppDispatch, useAppSelector } from 'hooks/redux';

import { IntroView, PendingView } from './HoldingScreen';
import LoginView from './LoginView';
import SearchView from './SearchView';
import { StepEnum } from './types';

import type { BillPresentmentProps } from '../actions';

const useStyles = makeStyles()((_theme) => ({
  content: {
    maxHeight: 592,
    minHeight: 431, // matches rootDialog minHeight so that dialogActions can sit at the bottom of the dialog correctly
    overflow: 'auto',
    padding: '12px 24px',
    position: 'relative',
    display: 'flex',
    '&.complete': {
      marginRight: 'auto',
      marginLeft: 'auto',
    },
  },
}));

const BillPresentmentDialog = (props: BillPresentmentProps) => {
  const { classes } = useStyles();
  const dispatch = useAppDispatch();

  const hasBillerAccounts = !!useAppSelector(billPresentmentSelectors.selectAllBillerAccounts)?.length;

  const { series } = props;
  const [GUID, setGUID] = useState<string>('');
  const [biller, setBiller] = useState<Biller | null>(null);
  const [enrollmentData, setEnrollmentData] = useState<EnrollResponse | null>(null);
  const [step, setStep] = useState<StepEnum>(hasBillerAccounts ? StepEnum.SEARCH : StepEnum.INTRO);
  const [open, setOpen] = useState(true);

  const handleClose = () => {
    setOpen(false);
    setStep(StepEnum.SEARCH);
    setEnrollmentData(null);
    dispatch(rootUiActions.removeDialog(`BPS-${series?.id}`));
  };

  const handleBack = () => {
    const sequence = [StepEnum.SEARCH, StepEnum.LOGIN];
    const currentIndex = sequence.indexOf(step);
    if (currentIndex > 0) {
      const previousStep = sequence[currentIndex - 1];
      setStep(previousStep);
    }
  };

  let pageToRender;
  let action = null;
  switch (step) {
    case StepEnum.INTRO:
      pageToRender = <IntroView />;
      action = (
        <Button id='bill-connect-continue' variant='contained' onClick={() => setStep(StepEnum.SEARCH)}>
          Got it
        </Button>
      );
      break;
    case StepEnum.SEARCH:
      pageToRender = (
        <SearchView setStep={setStep} setBiller={setBiller} setEnrollment={setEnrollmentData} setGUID={setGUID} />
      );
      break;
    case StepEnum.LOGIN:
      pageToRender = (
        <LoginView enrollment={enrollmentData} biller={biller} setSteps={setStep} GUID={GUID} series={series} />
      );
      break;
    case StepEnum.PENDING:
      pageToRender = <PendingView />;
      action = (
        <Button
          id='bill-connect-finish'
          variant='outlined'
          onClick={() => {
            // wait half a second and then fetch updated billerAccount and model (billers not hooked up to bump server)
            setTimeout(() => {
              dispatch(billPresentmentActions.fetch());
              dispatch(scheduledTransactionsActions.getScheduledTransactions());
            }, 500);
            handleClose();
          }}
        >
          Close
        </Button>
      );
      break;
    // complete may never be needed
    // case StepEnum.COMPLETE:
    //   pageToRender = <CompleteView />;
    //   break;
    default:
      pageToRender = null;
      break;
  }

  return (
    <StdDialog
      open={open}
      onBack={handleBack}
      onClose={handleClose}
      sharedcomponentid='BILL_PRESENTMENT_DIALOG'
      showBackButton={step === StepEnum.LOGIN}
      size='md'
      title='Connect to Account'
    >
      <div className={classes.content}>{pageToRender}</div>
      {action && <DialogActions>{action}</DialogActions>}
    </StdDialog>
  );
};

export default BillPresentmentDialog;
